import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import AuthLayout from './AuthLayout'

function useTitle(title) {
  useEffect(() => {
    const prev = document.title
    document.title = title
    return () => { document.title = prev }
  })
}

const s = {
  heading: {
    fontSize: '1.7rem',
    fontWeight: 700,
    color: '#000',
    marginBottom: 6,
    letterSpacing: '-0.02em',
  },
  sub: {
    fontSize: '0.95rem',
    color: '#64748b',
    marginBottom: 32,
    lineHeight: 1.65,
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    border: '1px solid #d1d9e0',
    borderRadius: 10,
    fontSize: '0.9rem',
    color: '#0f172a',
    outline: 'none',
    background: '#fff',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    padding: '11px 0',
    background: '#2fba01',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 8,
    transition: 'background 0.2s',
    letterSpacing: '0.01em',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    color: '#dc2626',
    fontSize: '0.85rem',
    padding: '10px 14px',
    marginBottom: 20,
  },
  link: {
    color: '#64748b',
    fontSize: '0.85rem',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  divider: {
    borderTop: '1px solid #f1f5f9',
    margin: '24px 0',
  },
}

function LoginScreen() {
  const [username, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [processing, setProcessing] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()

  // OAuth 2.0 return: when an agent redirects the user here from /oauth/authorize
  // because they weren't logged in, we bounce them back after login completes.
  const oauthReturn = new URLSearchParams(window.location.search).get('oauth_return')

  useTitle('Login — SchedPilot')

  // After login, send users who don't have an active membership straight to
  // billing so it's clear they need to start a membership/trial. Users with an
  // active membership land on the dashboard as usual.
  async function getPostLoginDestination() {
    try {
      const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/getAllInformation', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json()
      const m = Array.isArray(data?.membership) ? data.membership[0] : data?.membership
      const hasActiveMembership =
        m && m.zeroMembership !== 'yes' && m.end_date_time &&
        new Date(m.end_date_time.replace(' ', 'T') + 'Z').getTime() > Date.now()
      return hasActiveMembership ? '/' : '/billing'
    } catch {
      // On any error, don't trap the user — fall back to the dashboard.
      return '/'
    }
  }

  function loginUser(credentials) {
    setProcessing(true)
    setShowError(false)
    fetch('https://api.schedpilot.com/wp-json/api/v1/executeLogin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    })
      .then(r => r.json())
      .then(async json => {
        if (json.response == 200) {
          localStorage.setItem('myLoggedInUser2', 'logged')
          localStorage.setItem('jwtToken', json.jwtToken)
          if (oauthReturn) {
            // Return the user to the OAuth consent page they came from
            window.location.href = decodeURIComponent(oauthReturn)
          } else {
            const dest = await getPostLoginDestination()
            window.location.href = dest
          }
        } else {
          setProcessing(false)
          setErrorMessage(json.reason)
          setShowError(true)
        }
      })
  }

  const handleSubmit = e => {
    e.preventDefault()
    loginUser({ email: username, password })
  }

  return (
    <AuthLayout>
      <p style={s.heading}>Welcome back</p>
      <p style={s.sub}>Sign in to your SchedPilot account</p>

      {showError && <div style={s.error}>{errorMessage}</div>}
      {processing && <LoadingSpinner />}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 18 }}>
          <label style={s.label}>Email address</label>
          <input
            style={s.input}
            type="text"
            placeholder="you@example.com"
            onChange={e => setUserName(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#84cc16'}
            onBlur={e => e.target.style.borderColor = '#d1d9e0'}
            required
          />
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={s.label}>Password</label>
          <input
            style={s.input}
            type="password"
            placeholder="••••••••"
            onChange={e => setPassword(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#84cc16'}
            onBlur={e => e.target.style.borderColor = '#d1d9e0'}
            required
          />
        </div>

        <div style={{ textAlign: 'right', marginBottom: 22 }}>
          <span style={s.link} onClick={() => navigate('/forgot-password')}>
            Forgot password?
          </span>
        </div>

        <button
          type="submit"
          style={s.btn}
          onMouseOver={e => e.target.style.background = '#27a001'}
          onMouseOut={e => e.target.style.background = '#2fba01'}
        >
          Sign in
        </button>
      </form>

      <div style={s.divider} />

      <p style={{ fontSize: '0.85rem', color: '#7d8fa3', textAlign: 'center', margin: 0 }}>
        Don't have an account?{' '}
        <a href="https://schedpilot.com/pricing/" style={{ color: '#475569', textDecoration: 'underline' }}>
          View pricing
        </a>
      </p>
    </AuthLayout>
  )
}

export default LoginScreen
